import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import type { PortfolioWithQuotes, HoldingWithQuote } from '../../services/PortfolioService';
import { ChartSkeleton } from '../ui/Skeleton';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface UserPortfolioChartProps {
  portfolios?: PortfolioWithQuotes[];
  loading?: boolean;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt$ = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

/** Generate every calendar date between start and today (inclusive) */
function dateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  while (cur <= endDay) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/** Label a date nicely */
function labelDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── component ───────────────────────────────────────────────────────────────

export const UserPortfolioChart: React.FC<UserPortfolioChartProps> = ({
  portfolios = [],
  loading = false,
}) => {
  // Flatten all holdings across all portfolios
  const allHoldings: HoldingWithQuote[] = useMemo(
    () => portfolios.flatMap(p => p.holdings),
    [portfolios]
  );

  const { labels, investedData, currentValueData, totalInvested, totalCurrentValue } = useMemo(() => {
    if (allHoldings.length === 0) {
      return { labels: [], investedData: [], currentValueData: [], totalInvested: 0, totalCurrentValue: 0 };
    }

    // Find the earliest purchase date across all holdings
    const earliest = allHoldings.reduce<Date>((min, h) => {
      const d = new Date(h.purchase_date);
      return d < min ? d : min;
    }, new Date(allHoldings[0].purchase_date));

    const today = new Date();
    const dates = dateRange(earliest, today);

    // For each date, calculate:
    //   investedAmount = sum of (shares * purchase_price) for all holdings bought ON or BEFORE that date
    //   currentValue   = investedAmount scaled by today's gain/loss ratio per holding
    //
    // We don't have historical prices, so current value on each past date is approximated as:
    //   currentValue(date) = investedAmount(date) * (totalCurrentValue / totalInvestedCost)
    // This gives a proportional line that reflects the real gain/loss ratio while showing
    // how the portfolio grew as positions were added over time.

    const totalInvestedCost = allHoldings.reduce((s, h) => s + h.shares * h.purchase_price, 0);
    const totalCurrentVal = portfolios.reduce((s, p) => s + p.totalValue, 0);
    const gainRatio = totalInvestedCost > 0 ? totalCurrentVal / totalInvestedCost : 1;

    const investedData: number[] = [];
    const currentValueData: number[] = [];
    const labels: string[] = [];

    for (const date of dates) {
      // Sum cost of all holdings purchased on or before this date
      const invested = allHoldings.reduce((sum, h) => {
        const purchaseDate = new Date(h.purchase_date);
        purchaseDate.setHours(0, 0, 0, 0);
        if (purchaseDate <= date) {
          return sum + h.shares * h.purchase_price;
        }
        return sum;
      }, 0);

      investedData.push(invested);
      // Scale current value proportionally — on the last date this equals the real totalValue
      currentValueData.push(invested * gainRatio);
      labels.push(labelDate(date));
    }

    return { labels, investedData, currentValueData, totalInvested: totalInvestedCost, totalCurrentValue: totalCurrentVal };
  }, [allHoldings, portfolios]);

  const totalGainLoss = totalCurrentValue - totalInvested;
  const totalGainLossPct = totalInvested > 0 ? (totalGainLoss / totalInvested) * 100 : 0;
  const isPositive = totalGainLoss >= 0;

  const valueColor = isPositive ? 'rgb(34,197,94)' : 'rgb(239,68,68)';
  const valueBg   = isPositive ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)';

  const chartData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Amount Invested',
        data: investedData,
        borderColor: 'rgb(59,130,246)',
        backgroundColor: 'rgba(59,130,246,0.06)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: 'rgb(59,130,246)',
        pointHoverBorderColor: 'white',
        pointHoverBorderWidth: 2,
        order: 2,
      },
      {
        label: 'Current Value',
        data: currentValueData,
        borderColor: valueColor,
        backgroundColor: valueBg,
        borderWidth: 2,
        fill: false,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: valueColor,
        pointHoverBorderColor: 'white',
        pointHoverBorderWidth: 2,
        order: 1,
      },
    ],
  }), [labels, currentValueData, investedData, valueColor, valueBg]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 12,
          boxHeight: 2,
          padding: 16,
          color: '#475569',
          font: { size: 11 },
          usePointStyle: true,
          pointStyle: 'line',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(10,14,26,0.95)',
        padding: 12,
        titleColor: '#64748b',
        bodyColor: '#f1f5f9',
        borderColor: '#1e2d45',
        borderWidth: 1,
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (ctx) => ` ${ctx.dataset.label}: ${fmt$(ctx.parsed.y)}`,
          afterBody: (items) => {
            const cv = items.find(i => i.dataset.label === 'Current Value')?.parsed.y ?? 0;
            const inv = items.find(i => i.dataset.label === 'Amount Invested')?.parsed.y ?? 0;
            if (inv === 0) return [];
            const gl = cv - inv;
            const pct = (gl / inv) * 100;
            return [``, ` P&L: ${fmt$(gl)} (${fmtPct(pct)})`];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 8,
          color: '#334155',
          font: { size: 11 },
          maxRotation: 0,
        },
        border: { display: false },
      },
      y: {
        position: 'right',
        grid: { color: 'rgba(255,255,255,0.04)' },
        border: { display: false, dash: [4, 4] },
        ticks: {
          color: '#334155',
          font: { size: 11 },
          callback: (v) => fmt$(v as number),
        },
      },
    },
  };

  // ── loading ──
  if (loading) {
    return (
      <div style={{ background: '#111827', borderRadius: 10, border: '1px solid #1e2d45', padding: '1.25rem' }}>
        <ChartSkeleton />
      </div>
    );
  }

  // ── empty ──
  if (portfolios.length === 0 || allHoldings.length === 0) {
    return (
      <div style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.05), rgba(139,92,246,0.05))', border: '1px solid rgba(59,130,246,0.12)', borderRadius: 10, padding: '2rem', textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.875rem' }}>
          <svg width="22" height="22" fill="none" stroke="#60a5fa" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#94a3b8', marginBottom: '0.375rem' }}>No portfolio data yet</h3>
        <p style={{ fontSize: '0.8125rem', color: '#334155' }}>Add holdings to a portfolio to see your performance chart.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#111827', borderRadius: 10, border: '1px solid #1a2235', padding: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.07em', color: '#1e3a5f', marginBottom: '0.375rem' }}>
              Portfolio Performance
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.625rem', flexWrap: 'wrap' as const }}>
              <span style={{ fontSize: '1.625rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.04em', fontFamily: "'JetBrains Mono', monospace" }}>
                {fmt$(totalCurrentValue)}
              </span>
              <span style={{ fontSize: '0.875rem', fontWeight: 700, color: isPositive ? '#10b981' : '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>
                {fmt$(totalGainLoss)} ({fmtPct(totalGainLossPct)})
              </span>
            </div>
          </div>

          {/* Summary pills */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' as const }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.3rem 0.75rem', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 8 }}>
              <span style={{ width: 14, height: 2, background: '#3b82f6', display: 'inline-block', borderRadius: 99 }} />
              <span style={{ fontSize: '0.72rem', color: '#475569' }}>Invested</span>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#60a5fa', fontFamily: "'JetBrains Mono', monospace" }}>{fmt$(totalInvested)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.3rem 0.75rem', background: isPositive ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${isPositive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)'}`, borderRadius: 8 }}>
              <span style={{ width: 14, height: 2, background: valueColor, display: 'inline-block', borderRadius: 99 }} />
              <span style={{ fontSize: '0.72rem', color: '#475569' }}>Value</span>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: valueColor, fontFamily: "'JetBrains Mono', monospace" }}>{fmt$(totalCurrentValue)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: 220 }}>
        <Line data={chartData} options={options} />
      </div>

      {/* Footer */}
      <div style={{ marginTop: '0.875rem', paddingTop: '0.75rem', borderTop: '1px solid #0f1929', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
          {allHoldings.length} holding{allHoldings.length !== 1 ? 's' : ''} across {portfolios.length} portfolio{portfolios.length !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div className="live-dot" style={{ width: 6, height: 6 }} />
          <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>Live prices</span>
        </div>
      </div>
    </div>
  );
};
